import * as THREE from 'three';
import { Environment } from '@react-three/drei';
import { Canvas, useThree } from '@react-three/fiber';
import { Suspense, useEffect, useState } from 'react';
import { useStore } from './store';

import AboutComponent from './components/AboutButton';
import AudioControls from './components/AudioControls'; 
import BookButton from './components/BookButton';
import CameraControls from './components/CameraControls';
import HomepageLogo from './components/HomepageLogo';
import SalonGallery from './components/SalonGallery';

import isMobile from './utils/isMobile';

import './App.css';

const isMobileDevice = isMobile();
const workerUrl = 'https://leah-salon-worker.mack-joshuaj.workers.dev';

const LoadingBar = ({ progress }) => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
      <div className="w-64 h-1 bg-white/10 rounded-full overflow-hidden">
        <div 
          className="h-full bg-white transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="mt-4 text-white/50 text-sm font-medium">
        {Math.round(progress)}%
      </div>
    </div>
  );
};

const checkAssetExists = (url) => {
  return fetch(url, { method: 'HEAD' })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return true;
    })
    .catch(error => {
      console.warn(`Asset check failed for ${url}:`, error);
      return false;
    });
};

const AssetPreloader = ({ onLoadComplete, children }) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const assets = [
      `${workerUrl}/salon-cubemap.png`, 
      '/bgm/bgm.mp3'              
    ];

    let loadedCount = 0;

    const loadAssets = async () => {
      try {
        const loadPromises = assets.map((url) => {
          return checkAssetExists(url).then(() => {
            loadedCount++;
            setLoadingProgress((loadedCount / assets.length) * 100);
          });
        });

        await Promise.all(loadPromises);
        setIsLoading(false);
        if (onLoadComplete) onLoadComplete();
      } catch (error) {
        console.error('Error preloading assets:', error);
        setTimeout(() => {
          setIsLoading(false);
          if (onLoadComplete) onLoadComplete();
        }, 2000);
      }
    };

    loadAssets();
  }, [onLoadComplete]);

  return (
    <>
      {isLoading && <LoadingBar progress={loadingProgress} />}
      {children}
    </>
  );
};

function MobileEnvironment() {
  const { scene, gl } = useThree();

  useEffect(() => {
    const canvas = document.createElement('canvas');
    const canvasContext = canvas.getContext('2d');
    const img = new Image();
    const cubemapUrl = `${workerUrl}/salon-cubemap.png`;
    img.crossOrigin = "anonymous";  // Enable CORS
    
    img.onload = () => {
      const faceSize = img.height / 3; 
      canvas.width = faceSize;
      canvas.height = faceSize;

      const extractFace = (x, y) => {
        canvasContext.clearRect(0, 0, faceSize, faceSize);
        canvasContext.drawImage(img, x * faceSize, y * faceSize, faceSize, faceSize, 0, 0, faceSize, faceSize);
        return canvas.toDataURL();
      };

      // Extract faces according to the provided cross pattern
      // CubeTextureLoader order: right(+x), left(-x), up(+y), down(-y), front(+z), back(-z)
      const right = extractFace(2, 1);   // +X face
      const left = extractFace(0, 1);    // -X face
      const up = extractFace(1, 0);      // +Y face
      const down = extractFace(1, 2);    // -Y face
      const front = extractFace(1, 1);   // +Z face
      const back = extractFace(3, 1);    // -Z face

      const cubeTexture = new THREE.CubeTextureLoader().load([
        right, left,    // +X, -X
        up, down,       // +Y, -Y
        front, back     // +Z, -Z
      ], undefined, undefined, (error) => {
        console.error('Error loading cubemap:', error);
      });

      cubeTexture.encoding = THREE.sRGBEncoding;
      
      scene.background = cubeTexture;
      scene.environment = cubeTexture;
      
      gl.physicallyCorrectLights = true;
      gl.toneMapping = THREE.ACESFilmicToneMapping;
      gl.toneMappingExposure = 1;
    };

    img.onerror = (error) => {
      console.error('Error loading image:', error);
    };

    img.src = cubemapUrl;

    return () => {
      if (scene.background) {
        scene.background.dispose();
        scene.background = null;
      }
      if (scene.environment) {
        scene.environment.dispose();
        scene.environment = null;
      }
    };
  }, [scene, gl]);

  return null;
}

function EnvironmentLoader() {
  if (isMobileDevice) {
    return (
      <>
        <MobileEnvironment />
        <MobileLighting />
      </>
    );
  }

  const hdrUrl = `${workerUrl}/salon_4k.hdr`;

  return (
    <Environment 
      files={hdrUrl}
      background
      blur={0.1}
      intensity={0.85}
    />
  );
}

function MobileLighting() {
  if (!isMobileDevice) return null;
  
  return (
    <>
      <ambientLight intensity={1.0} />
      <pointLight position={[10, 10, 10]} intensity={3.0} color="#FFFFFF" />
      <pointLight position={[-10, 5, -10]} intensity={1.5} color="#9370DB" />
      <pointLight position={[0, -10, 0]} intensity={1.0} color="#4B0082" />
    </>
  );
}

function useOrientation() {
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isPortrait;
}

const mobileSettings = {
  cameraZ: 12,
  fov: 60,
  scale: 0.75
};

const desktopSettings = {
  cameraZ: 8,
  fov: 50,
  scale: 1
};

function EnvironmentRotation() {
  const { scene } = useThree();
  const [isAnimating] = useState(true);
  
  useEffect(() => {
    const envNode = scene.children.find(child => child.name === 'Environment');
    if (!envNode) return;

    const rotationSpeed = 0.001;
    let animationId;

    const animate = () => {
      if (isAnimating && envNode) {
        envNode.rotation.y += rotationSpeed;
      }
      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [scene, isAnimating]);

  return null;
}

function LoadingOverlay() {
  const isAnimating = useStore((state) => state.isAnimating);
  
  return (
    <div 
      className={`fixed inset-0 bg-pink-200 z-40 transition-opacity duration-1000 ${
        isAnimating ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    />
  );
}

const EnterButton = ({ isVisible }) => {
  const startAnimation = useStore((state) => state.startAnimation);
  const setAudio = useStore((state) => state.setAudio);

  const handleClick = () => {
    startAnimation();
    const bgMusic = new Audio('/bgm/bgm.mp3');
    bgMusic.volume = 0.4;
    bgMusic.loop = true; 
    bgMusic.play().catch(error => {
      console.log("Audio play failed:", error);
    });
    
    setAudio(bgMusic);
    
    document.getElementById('enter-button').style.display = 'none';
  };

  if (!isVisible) return null;

  return (
    <div 
      id="enter-button" 
      className="fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-opacity duration-500 opacity-0 animate-fade-in"
    >
      <button
        onClick={handleClick}
        className="relative group bg-white bg-opacity-50 backdrop-blur-sm text-pink-400 border border-white/20 px-8 py-4 rounded-lg
          transition-all duration-300 ease-out hover:bg-white hover:text-pink-400 hover:border-transparent
          hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] hover-float"
      >
        <span className="text-lg font-medium tracking-wider">ENTER <br/> AESTHETIC MOMENT</span>
        <div className="absolute inset-0 bg-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      </button>
    </div>
  );
};

function App() {
  const isPortrait = useOrientation();
  const [assetsLoaded, setAssetsLoaded] = useState(false);

  return (
    <div style={{ width: '100%', height: '100vh', position: 'fixed' }}>
       <div className="absolute w-full top-[10%] lg:top-[5%] text-center z-10 px-4">
        <h1 className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-wider leading-tight drop-shadow-lg">
          PREMIERE<br className="sm:hidden" /> SALON<br className="sm:hidden" /> EXPERIENCE
        </h1>
      </div>
      <AssetPreloader onLoadComplete={() => setAssetsLoaded(true)}>
        <LoadingOverlay />
        <EnterButton isVisible={assetsLoaded} />
        <AboutComponent />
        <SalonGallery />
        <BookButton />
        <AudioControls />
        <Suspense fallback={null}>
          <Canvas
            camera={{ 
              position: [0, 0, isPortrait ? mobileSettings.cameraZ : desktopSettings.cameraZ], 
              fov: isPortrait ? mobileSettings.fov : desktopSettings.fov 
            }}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
            dpr={[1, 2]}
            gl={{ 
              powerPreference: "high-performance",
              alpha: false,
              antialias: true,
              stencil: false,
              depth: true
            }}
          >
            <color attach="background" args={['#202020']} />
            <ambientLight intensity={0.1} />
            <pointLight position={[10, 10, 10]} intensity={10.0} color="#9370DB" />
            
            <Suspense fallback={null}>
              <EnvironmentLoader />
              <CameraControls mobileSettings={mobileSettings} desktopSettings={desktopSettings} />
              <EnvironmentRotation />
              <group 
                scale={isPortrait ? mobileSettings.scale : desktopSettings.scale}
                position={isPortrait ? [0, 1.2, 0] : [0, 0, 0]} // Move the logo 20% higher on mobile
>
                <HomepageLogo />
              </group>
            </Suspense>
          </Canvas>
        </Suspense>
      </AssetPreloader>
    </div>
  );
}

export default App;